import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, UsersThree, Umbrella, X, PencilSimple, Clock, Warning } from "@phosphor-icons/react";

const LEAVE_LABEL = { odihna: "CO", medical: "Medical" };

export default function MontajTeamsPanel({ project, reload }) {
  const [montatori, setMontatori] = useState([]);
  const [name, setName] = useState("");
  const [leader, setLeader] = useState("");
  const [notes, setNotes] = useState("");
  const [members, setMembers] = useState([]); // {name, user_id}
  const [manual, setManual] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [timp, setTimp] = useState({});
  const [tzile, setTzile] = useState("");
  const [tore, setTore] = useState("");
  const [tstart, setTstart] = useState("");
  const [tnote, setTnote] = useState("");

  const loadTimp = () => api.get(`/projects/${project.id}/montaj-timp`).then((r) => {
    const d = r.data || {};
    setTimp(d);
    setTzile(d.montaj_durata_zile ?? "");
    setTore(d.montaj_durata_ore ?? "");
    setTstart((d.montaj_start_at || "").slice(0, 10));
    setTnote(d.montaj_overrun_note || "");
  }).catch(() => {});

  const saveTimp = async () => {
    try {
      const { data } = await api.put(`/projects/${project.id}/montaj-timp`, {
        durata_zile: tzile === "" ? null : Number(tzile),
        durata_ore: tore === "" ? null : Number(tore),
        start_at: tstart || null, overrun_note: tnote,
      });
      setTimp(data); toast.success("Timp montaj salvat");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { loadTimp(); /* eslint-disable-next-line */ }, [project.id]);

  useEffect(() => { api.get("/montatori").then((r) => setMontatori(r.data)).catch(() => {}); }, []);

  const addMontator = (id) => {
    const m = montatori.find((x) => x.id === id);
    if (!m || members.some((x) => x.user_id === id)) return;
    setMembers([...members, { name: m.name, user_id: m.id }]);
  };
  const addManual = () => {
    if (!manual.trim()) return;
    setMembers([...members, { name: manual.trim(), user_id: null }]);
    setManual("");
  };
  const removeMember = (i) => setMembers(members.filter((_, idx) => idx !== i));

  const resetForm = () => { setName(""); setLeader(""); setNotes(""); setMembers([]); setEditId(null); };
  const startEdit = (t) => {
    setEditId(t.id); setName(t.name || ""); setLeader(t.leader || "");
    setNotes(t.notes || ""); setMembers((t.members || []).map((m) => ({ name: m.name, user_id: m.user_id || null })));
  };

  const save = async () => {
    if (!name.trim() && members.length === 0) { toast.error("Adaugă un nume de echipă sau membri"); return; }
    setSaving(true);
    try {
      const payload = { name, leader, notes, members, size: members.length };
      if (editId) {
        await api.put(`/projects/${project.id}/montaj-teams/${editId}`, payload);
        toast.success("Echipă actualizată");
      } else {
        await api.post(`/projects/${project.id}/montaj-teams`, payload);
        toast.success("Echipă de montaj adăugată");
      }
      resetForm();
      reload && reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delTeam = async (tid) => {
    try { await api.delete(`/projects/${project.id}/montaj-teams/${tid}`); toast.success("Echipă ștearsă"); reload && reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const teams = project.montaj_teams || [];

  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="montaj-teams-panel">
      <div className="space-y-4 lg:col-span-2">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 flex items-center gap-2 font-head text-lg font-bold"><UsersThree size={20} /> Echipe de montaj</h3>
          <div className="space-y-3">
            {teams.length === 0 && <p className="text-sm text-muted-foreground">Nicio echipă de montaj introdusă. Adaugă prima echipă din dreapta.</p>}
            {teams.map((t) => (
              <div key={t.id} className="rounded-lg border border-border p-4" data-testid={`montaj-team-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{t.name} <span className="ml-1 text-xs text-muted-foreground">({t.size} pers.)</span></div>
                    {t.leader && <div className="text-xs text-muted-foreground">Șef echipă: {t.leader}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(t)} className="text-muted-foreground hover:text-primary" data-testid={`montaj-team-edit-${t.id}`} title="Modifică echipa"><PencilSimple size={16} /></button>
                    <button onClick={() => delTeam(t.id)} className="text-muted-foreground hover:text-red-400" data-testid={`montaj-team-del-${t.id}`}><Trash size={16} /></button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(t.members || []).map((m, i) => (
                    <span key={i} className="rounded-full bg-white/5 px-2.5 py-1 text-xs">{m.name}</span>
                  ))}
                </div>
                {t.notes && <p className="mt-2 text-xs text-muted-foreground">{t.notes}</p>}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6" data-testid="montaj-timp-card">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Clock size={16} className="text-primary" /> Timp de montaj</h3>
          {timp.montaj_overrun && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/[0.06] px-3 py-2 text-xs font-semibold text-red-300" data-testid="montaj-overrun-warning">
              <Warning size={15} weight="fill" /> Timpul de montaj a fost DEPĂȘIT{timp.montaj_deadline ? ` (termen: ${new Date(timp.montaj_deadline).toLocaleString("ro-RO")})` : ""}. Adaugă o explicație mai jos.
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="grid gap-1"><Label className="text-xs">Durată (zile)</Label><Input type="number" min="0" value={tzile} onChange={(e) => setTzile(e.target.value)} data-testid="montaj-timp-zile" /></div>
            <div className="grid gap-1"><Label className="text-xs">Durată (ore)</Label><Input type="number" min="0" value={tore} onChange={(e) => setTore(e.target.value)} data-testid="montaj-timp-ore" /></div>
            <div className="grid gap-1"><Label className="text-xs">Start montaj</Label><Input type="date" value={tstart} onChange={(e) => setTstart(e.target.value)} data-testid="montaj-timp-start" /></div>
          </div>
          {timp.montaj_deadline && !timp.montaj_overrun && <p className="mt-2 text-xs text-muted-foreground">Termen estimat finalizare montaj: {new Date(timp.montaj_deadline).toLocaleString("ro-RO")}</p>}
          <div className="mt-2 grid gap-1"><Label className="text-xs">Explicație depășire (dacă e cazul)</Label><Textarea value={tnote} onChange={(e) => setTnote(e.target.value)} placeholder="De ce s-a depășit timpul de montaj?" data-testid="montaj-timp-note" /></div>
          <Button size="sm" className="mt-3" onClick={saveTimp} data-testid="montaj-timp-save">Salvează timp montaj</Button>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Umbrella size={16} className="text-amber-400" /> Disponibilitate montatori (concedii)</h3>
          <div className="space-y-2">
            {montatori.length === 0 && <p className="text-sm text-muted-foreground">Niciun montator în departamentul Montaj încă.</p>}
            {montatori.map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-3 rounded-lg border border-border px-3 py-2" data-testid={`montator-${m.id}`}>
                <div className="text-sm font-medium">{m.name}{m.on_leave_now && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">ÎN CONCEDIU</span>}</div>
                <div className="text-right text-[11px] text-muted-foreground">
                  {(m.leaves || []).length === 0 ? "fără concedii" : m.leaves.map((lv, i) => (
                    <div key={i}>{LEAVE_LABEL[lv.type] || lv.type}: {lv.start} → {lv.end}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="mb-4 font-head text-lg font-bold">{editId ? "Modifică echipa" : "Echipă nouă"}</h3>
        <div className="space-y-3">
          <div className="grid gap-1.5"><Label>Nume echipă</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Echipa A" data-testid="team-name" /></div>
          <div className="grid gap-1.5"><Label>Șef echipă</Label><Input value={leader} onChange={(e) => setLeader(e.target.value)} placeholder="Nume șef echipă" data-testid="team-leader" /></div>
          <div className="grid gap-1.5">
            <Label>Adaugă montator</Label>
            <Select value="" onValueChange={addMontator}>
              <SelectTrigger data-testid="team-add-montator"><SelectValue placeholder="Din departamentul Montaj…" /></SelectTrigger>
              <SelectContent>
                {montatori.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.on_leave_now ? " (în concediu)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="…sau nume liber" onKeyDown={(e) => e.key === "Enter" && addManual()} data-testid="team-manual-member" />
            <Button variant="outline" size="icon" onClick={addManual} data-testid="team-add-manual"><Plus size={16} /></Button>
          </div>
          {members.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {members.map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs text-primary">
                  {m.name}<button onClick={() => removeMember(i)}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="grid gap-1.5"><Label>Note</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Feedback montaj, lipsuri, observații…" data-testid="team-notes" /></div>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="team-save-btn">{saving ? "Se salvează…" : (editId ? "Salvează modificările" : "Adaugă echipa")}</Button>
          {editId && <Button variant="outline" onClick={resetForm} className="w-full" data-testid="team-cancel-btn">Anulează editarea</Button>}
        </div>
      </div>
    </div>
  );
}
